import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Table,
  Group,
  Badge,
  TextInput,
  Stack,
  Alert,
  Paper,
  Button,
  Drawer,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSearch,
  IconAlertCircle,
  IconRefresh,
  IconPhoto,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import { useFogImages, type FogImage } from '@/hooks';
import { FogLoadingOverlay } from '@/components/fog';

export function FogImagesPage() {
  const { data, isLoading, error, refetch, isRefetching } = useFogImages();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState<FogImage | null>(null);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [pageVisible, setPageVisible] = useState(false);
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    setPageVisible(true);
  }, []);

  // Animate rows
  useEffect(() => {
    if (data?.images) {
      data.images.forEach((_, index) => {
        setTimeout(() => {
          setVisibleRows((prev) => new Set([...prev, index]));
        }, index * 50);
      });
    }
  }, [data?.images]);

  const images = data?.images || [];

  // Filter images
  const filteredImages = images.filter((image) => {
    const matchesSearch =
      !searchQuery ||
      image.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      image.osName?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleViewDetails = (image: FogImage) => {
    setSelectedImage(image);
    openDrawer();
  };

  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <FogLoadingOverlay message="Loading FOG Images..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load FOG images: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      size="xl"
      py="md"
      style={{
        opacity: pageVisible ? 1 : 0,
        transform: pageVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>FOG Images</Title>
          <Text c="dimmed" size="sm">
            {images.length} images available for deployment
          </Text>
        </div>
        <Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={isRefetching}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Search */}
      <Paper withBorder p="md" mb="md" radius="md">
        <TextInput
          placeholder="Search images..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Paper>

      {/* Images Table */}
      <Paper withBorder radius="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Operating System</Table.Th>
              <Table.Th>Size</Table.Th>
              <Table.Th>Last Deployed</Table.Th>
              <Table.Th>Status</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredImages.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text ta="center" c="dimmed" py="xl">
                    No images found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filteredImages.map((image, index) => (
                <Table.Tr
                  key={image.id}
                  style={{
                    opacity: visibleRows.has(index) ? 1 : 0,
                    transform: visibleRows.has(index) ? 'translateX(0)' : 'translateX(-10px)',
                    transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleViewDetails(image)}
                >
                  <Table.Td>
                    <Group gap="xs">
                      <IconPhoto size={16} />
                      <Text fw={500}>{image.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    {image.osName ? (
                      <Badge variant="light" color="blue">
                        {image.osName}
                      </Badge>
                    ) : (
                      <Text c="dimmed" size="sm">-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{image.size || '-'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {image.deployed
                        ? new Date(image.deployed).toLocaleDateString()
                        : 'Never'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {image.isEnabled !== false ? (
                      <Badge color="green" leftSection={<IconCheck size={12} />}>
                        Enabled
                      </Badge>
                    ) : (
                      <Badge color="gray" leftSection={<IconX size={12} />}>
                        Disabled
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Image Details Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="Image Details"
        position="right"
        size="lg"
      >
        {selectedImage && (
          <Stack gap="md">
            <Paper withBorder p="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconPhoto size={20} />
                <Title order={4}>{selectedImage.name}</Title>
              </Group>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">ID</Text>
                  <Text size="sm">{selectedImage.id}</Text>
                </Group>
                {selectedImage.osName && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Operating System</Text>
                    <Text size="sm">{selectedImage.osName}</Text>
                  </Group>
                )}
                {selectedImage.path && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Path</Text>
                    <Text size="sm" ff="monospace">{selectedImage.path}</Text>
                  </Group>
                )}
                {selectedImage.size && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Size</Text>
                    <Text size="sm">{selectedImage.size}</Text>
                  </Group>
                )}
                {selectedImage.description && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Description</Text>
                    <Text size="sm">{selectedImage.description}</Text>
                  </Group>
                )}
                {selectedImage.createdTime && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Created</Text>
                    <Text size="sm">{new Date(selectedImage.createdTime).toLocaleString()}</Text>
                  </Group>
                )}
                {selectedImage.deployed && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Last Deployed</Text>
                    <Text size="sm">{new Date(selectedImage.deployed).toLocaleString()}</Text>
                  </Group>
                )}
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Status</Text>
                  {selectedImage.isEnabled !== false ? (
                    <Badge color="green">Enabled</Badge>
                  ) : (
                    <Badge color="gray">Disabled</Badge>
                  )}
                </Group>
              </Stack>
            </Paper>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}
